<script setup lang="ts">
import {computed} from "vue";
import {ChevronDown, CircleAlert, Settings2} from "@lucide/vue";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/core/components/ui/collapsible";
import {Skeleton} from "@/core/components/ui/skeleton";
import AssistantParameterField from "@/ai-assistant/components/AssistantParameterField.vue";
import useAssistantSettingsForm from "@/ai-assistant/composables/useAssistantSettingsForm";
import {HEALTH_JOURNEY_GROUP, PLANNING_ASSISTANT} from "@/ai-assistant/constants";
import {AssistantParameterField as ParameterField} from "@/ai-assistant/interfaces";

/**
 * The pharmacy's own rules for laying a journey out: how far apart acts sit,
 * which of them may share a visit, how long such a visit may run.
 *
 * Preferences, not guarantees — the regulatory delay a program states between
 * two of its acts comes from the catalogue and always wins, which is why every
 * spacing says so under itself. They are saved for the whole officine as soon
 * as they are touched, so the next journey starts from them.
 */
const emits = defineEmits<{
    /** A rule changed: the plan on screen was computed under the old ones. */
    (e: 'changed'): void;
}>();

const open = defineModel<boolean>('open', {default: false});

const {schema, values, isLoading, failed, editValue} = useAssistantSettingsForm(PLANNING_ASSISTANT);

const fields = computed<ParameterField[]>(() =>
    schema.value.filter((field) => field.group === HEALTH_JOURNEY_GROUP)
);

/**
 * A spacing cleared to nothing is no spacing, not an unbounded one — the
 * opposite of what the generic field says for a daily cap. The server names
 * every duration in days with the same suffix, and anything it adds later
 * falls back on the generic wording rather than on a guess.
 */
const emptyLabelFor = (field: ParameterField) => field.key.endsWith('_days') ? 'Aucun' : 'Illimité';

/** The current rules in one line, so the panel says something while folded. */
const summary = computed<string>(() => {
    const spacings = fields.value
        .filter((field) => field.type === 'integer' && field.key.endsWith('_days'))
        .map((field) => values.value[field.key])
        .filter((value): value is number => typeof value === 'number');

    const combined = fields.value
        .filter((field) => field.type === 'boolean' && values.value[field.key])
        .length;

    if (!spacings.length && !combined) {
        return 'Espacements et cumuls par défaut';
    }

    const spacing = spacings.length ? `Espacements : ${Math.min(...spacings)} à ${Math.max(...spacings)} jours` : null;

    return [spacing, `${combined} règle${combined > 1 ? 's' : ''} de cumul active${combined > 1 ? 's' : ''}`]
        .filter(Boolean)
        .join(' · ');
});

const edit = (key: string, value: ParameterField['default']) => {
    editValue(key, value ?? null);
    emits('changed');
};
</script>

<template>
  <Collapsible v-model:open="open" class="rounded-lg bg-white shadow">
    <CollapsibleTrigger
      class="flex w-full items-center gap-2 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Settings2 class="size-4 shrink-0 text-primary" />
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold text-primary">Règles de planification</span>
        <span class="block truncate text-xs text-gray-500">{{ summary }}</span>
      </span>
      <ChevronDown class="size-4 shrink-0 text-gray-400 transition-transform" :class="{'rotate-180': open}" />
    </CollapsibleTrigger>
    <CollapsibleContent class="border-t border-slate-100 px-4 py-3">
      <div v-if="isLoading && !fields.length" class="flex flex-col gap-2">
        <Skeleton v-for="index in 4" :key="index" class="h-8 w-full" />
      </div>

      <div
        v-else-if="failed"
        class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-xs leading-snug text-gray-dark"
      >
        <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        Les règles n'ont pas pu être chargées. Le parcours sera proposé avec les règles par défaut.
      </div>

      <p v-else-if="!fields.length" class="text-xs text-gray-500">
        Cette officine n'a pas encore de règles de parcours.
      </p>

      <div v-else class="flex flex-col gap-2.5">
        <div v-for="field in fields" :key="field.key">
          <AssistantParameterField
            :field="field"
            :empty-label="emptyLabelFor(field)"
            :model-value="values[field.key] ?? null"
            @update:model-value="edit(field.key, $event)"
          />
          <p v-if="field.help" class="mt-1 px-0.5 text-[10.5px] leading-snug text-gray-dark/60">
            {{ field.help }}
          </p>
        </div>
        <p class="mt-1 text-[10.5px] leading-snug text-gray-dark/60">
          Ces règles valent pour toute l'officine et sont enregistrées dès que vous les modifiez.
        </p>
      </div>
    </CollapsibleContent>
  </Collapsible>
</template>
